import type { Messages } from './en.js';

/** Español — terminología alineada con la landing (Cronología, Duplicados…). */
export const es: Messages = {
  // ── nav ────────────────────────────────────────────────────────────────
  'nav.timeline': 'Cronología',
  'nav.search': 'Buscar',
  'nav.duplicates': 'Duplicados',
  'nav.collections': 'Colecciones',
  'nav.import': 'Importar',
  'nav.trash': 'Papelera',
  'nav.settings': 'Ajustes',

  // ── common actions ─────────────────────────────────────────────────────
  'common.restore': 'Restaurar',
  'common.delete': 'Eliminar',
  'common.cancel': 'Cancelar',
  'common.save': 'Guardar',
  'common.close': 'Cerrar',
  'common.upgrade': 'Mejorar',
  'common.upgradeToPro': 'Pasar a Pro',
  'common.search': 'Buscar',
  'common.tryAgain': 'Reintentar',
  'common.loading': 'Cargando…',
  'common.back': 'Atrás',
  'common.skip': 'Omitir',
  'common.verify': 'Verificar',
  'common.resend': 'Reenviar',
  'common.free': 'Free',
  'common.pro': 'Pro',
  'common.clearFilters': 'Quitar filtros',
  'common.addToChrome': 'Añadir a Chrome',
  'toast.genericError': 'Algo salió mal. Inténtalo de nuevo.',

  // ── plural units ───────────────────────────────────────────────────────
  'unit.tabs.one': '{count} pestaña',
  'unit.tabs.few': '{count} pestañas',
  'unit.tabs.many': '{count} pestañas',
  'unit.tabs.other': '{count} pestañas',
  'unit.sessions.one': '{count} sesión',
  'unit.sessions.few': '{count} sesiones',
  'unit.sessions.many': '{count} sesiones',
  'unit.sessions.other': '{count} sesiones',
  'unit.items.one': '{count} elemento',
  'unit.items.few': '{count} elementos',
  'unit.items.many': '{count} elementos',
  'unit.items.other': '{count} elementos',
  'unit.entries.one': '{count} entrada',
  'unit.entries.few': '{count} entradas',
  'unit.entries.many': '{count} entradas',
  'unit.entries.other': '{count} entradas',
  'unit.copies.one': '{count} copia',
  'unit.copies.few': '{count} copias',
  'unit.copies.many': '{count} copias',
  'unit.copies.other': '{count} copias',
  'unit.browsers.one': '{count} navegador',
  'unit.browsers.few': '{count} navegadores',
  'unit.browsers.many': '{count} navegadores',
  'unit.browsers.other': '{count} navegadores',

  // ── app shell ──────────────────────────────────────────────────────────
  'shell.searchPlaceholder': 'Busca en tu vault…',
  'shell.saved': 'GUARDADO',
  'shell.toggleTheme': 'Cambiar tema',

  // ── timeline ───────────────────────────────────────────────────────────
  'timeline.empty.title': 'Tu vault está vacío — por ahora',
  'timeline.empty.body':
    'Barre una ventana con la extensión de Nomad Tabs o rescata tu viejo cementerio de OneTab. Todo aterriza aquí, y todo se puede buscar.',
  'timeline.empty.import': 'Importa tus pestañas',
  'timeline.empty.importOneTab': 'Importar desde OneTab',
  'timeline.empty.tip': 'TIP: ⇧⌘S BARRE LA VENTANA ACTUAL DESDE LA EXTENSIÓN',
  'timeline.noMatch.title': 'Ninguna sesión coincide con estos filtros',
  'timeline.noMatch.body': 'Prueba un rango de fechas más amplio, otro navegador o desactiva «solo con contenido».',
  'timeline.loadOlder': 'Cargar sesiones anteriores',
  'timeline.toast.sessionDeleted': 'Sesión eliminada',
  'timeline.toast.loadFailed': 'No se pudo cargar esa sesión',
  'timeline.delete.title': '¿Eliminar esta sesión?',
  'timeline.delete.subtitle': 'Elimina «{title}» y sus {tabs} guardadas. No se puede deshacer.',
  'timeline.delete.body': 'Si cambias de opinión, aún puedes recuperar sus pestañas desde el historial del navegador.',
  'timeline.delete.confirm': 'Eliminar sesión',
  'timeline.delete.fallbackTitle': 'esta sesión',
  'timeline.offline.title': 'Estás sin conexión',
  'timeline.offline.body': 'Tu vault funciona en local — la sincronización se reanuda sola cuando vuelvas a estar en línea.',
  'timeline.error.title': 'No se pudo cargar tu cronología',
  'timeline.error.body': 'Algo falló al conectar con el vault. Revisa tu conexión e inténtalo de nuevo.',

  // ── search ─────────────────────────────────────────────────────────────
  'search.mode.text': 'Texto',
  'search.mode.hybrid': 'Híbrido',
  'search.mode.semantic': 'Semántico',
  'search.placeholder': 'Busca en tu vault…',
  'search.clear': 'Borrar búsqueda',
  'search.status.counted': 'BUSCANDO EN {count} PESTAÑAS',
  'search.status.default': 'BUSCANDO EN TU VAULT',
  'search.empty.title': 'Busca en todo lo que has guardado',
  'search.empty.body':
    'Encuentra una pestaña por su título, su dirección o las palabras de la página. El modo semántico la encuentra por su significado.',
  'search.noMatch.title': 'Sin resultados para «{q}»',
  'search.noMatch.body': 'Prueba con menos palabras u otras distintas, cambia al modo Híbrido o quita los filtros.',
  'search.failed.eyebrow': '— LA BÚSQUEDA FALLÓ',
  'search.failed.body': 'Algo falló al conectar con tu vault.',
  'search.failed.retryHint': 'Inténtalo otra vez.',
  'search.results': '{count} RESULTADOS',
  'search.sort.relevance': 'Relevancia',
  'search.sort.date': 'Fecha',
  'search.loadMore': 'Cargar más resultados',
  'search.bestMatch': 'MEJOR RESULTADO',
  'search.openedIn': 'LO ABRISTE EN {browser}',
  'search.facet.browser': 'NAVEGADOR',
  'search.facet.date': 'FECHA',
  'search.facet.topDomains': 'DOMINIOS TOP',
  'search.range.any': 'Cualquier fecha',
  'search.range.24h': '24 horas',
  'search.range.7d': '7 días',
  'search.range.30d': '30 días',
  'search.range.1y': '12 meses',
  'search.upsell.eyebrow': 'BÚSQUEDA SEMÁNTICA · PRO',
  'search.upsell.title': 'La búsqueda semántica es una función Pro',
  'search.upsell.body':
    'Encuentra pestañas por su significado, no solo por palabras clave — «ese artículo sobre los recortes de tipos del banco central» en vez del título exacto. Pasa a Pro y busca en todo lo que hayas guardado.',
  'search.upsell.seePlans': 'Ver planes Pro',
  'search.upsell.useText': 'Buscar con texto',

  // ── duplicates ─────────────────────────────────────────────────────────
  'dupes.title': 'Centro de duplicados',
  'dupes.summary.duplicates': '{count} DUPLICADOS',
  'dupes.summary.ram': '≈ {ram} DE RAM POR LIBERAR',
  'dupes.closeSelected': 'Cerrar seleccionados ({count})',
  'dupes.keepNewest': 'Guardar la más nueva, cerrar el resto',
  'dupes.error.title': 'No se pudieron cargar tus duplicados',
  'dupes.toast.closed': 'Cerradas {copies}',
  'dupes.toast.closedBody': 'La copia más reciente sigue abierta — todas las copias siguen guardadas en tu vault.',

  // ── collections ────────────────────────────────────────────────────────
  'collections.title': 'Colecciones',
  'collections.subtitle': 'Ordenadas por IA — renombra o fusiona cuando quieras',
  'collections.new': 'Nueva colección',
  'collections.banner.title': 'Las colecciones automáticas con IA son una función Pro.',
  'collections.banner.body': 'Aun así puedes crear y organizar colecciones a mano — eso es gratis, para siempre.',
  'collections.empty.title': 'Aún no hay colecciones',
  'collections.empty.body':
    'Agrupa pestañas por tema. Crea una a mano — Pro además ordena tu vault en colecciones automáticamente.',
  'collections.error.title': 'No se pudieron cargar tus colecciones',

  // ── import ─────────────────────────────────────────────────────────────
  'import.title': 'Importa tus viejos cementerios de pestañas',
  'import.drop.title': 'Suelta aquí tu archivo de exportación',
  'import.drop.body': 'OneTab, Session Buddy o HTML de marcadores —',
  'import.drop.browse': 'elegir archivo',
  'import.readyEyebrow': 'LISTO PARA IMPORTAR',

  // ── settings ───────────────────────────────────────────────────────────
  'settings.title': 'Ajustes',
  'settings.section.account': 'CUENTA Y SYNC',
  'settings.section.browsers': 'NAVEGADORES CONECTADOS',
  'settings.section.capture': 'REGLAS DE CAPTURA',
  'settings.section.sharing': 'COMPARTIR',
  'settings.section.storage': 'ALMACENAMIENTO',
  'settings.section.appearance': 'APARIENCIA',
  'settings.section.language': 'IDIOMA',
  'settings.section.about': 'ACERCA DE',
  'settings.section.danger': 'ZONA DE PELIGRO',
  'settings.account.private': 'PRIVADO Y SEGURO',
  'settings.account.yourAccount': 'Tu cuenta',
  'settings.account.proPlan': 'PLAN PRO',
  'settings.account.freePlan': 'PLAN FREE',
  'settings.account.managePlan': 'Gestionar plan',
  'settings.account.seePro': 'Ver Pro',
  'settings.account.privacyNote':
    'Tus páginas guardadas son privadas — solo tú puedes verlas. Las mantenemos a salvo y fuera del alcance de todos los demás.',
  'settings.account.billing': 'Facturación y pagos',
  'settings.browsers.syncAuto': 'Los demás se sincronizan solos',
  'settings.browsers.thisBrowser': 'Este navegador',
  'settings.browsers.webApp': 'APP WEB',
  'settings.browsers.activeNow': 'ACTIVO AHORA',
  'settings.browsers.installHint':
    'Instala la extensión en tus otros navegadores — aparecerán aquí y se sincronizarán automáticamente.',
  'settings.capture.onDevice': 'EN ESTE DISPOSITIVO',
  'settings.capture.saveContent': 'Guardar el contenido de la página para buscar',
  'settings.capture.saveContentDesc': 'Solo texto — almacenado en privado para la búsqueda de texto completo',
  'settings.capture.skipIncognito': 'Omitir ventanas de incógnito',
  'settings.capture.neverOn': 'Nunca capturar contenido en:',
  'settings.capture.addSite': '+ añadir sitio',
  'settings.capture.localNote':
    'Por ahora estas reglas viven en este navegador — pronto se sincronizarán entre dispositivos.',
  'settings.sharing.readOnly': 'Enlaces públicos de solo lectura',
  'settings.sharing.collection': 'Colección',
  'settings.sharing.sweep': 'Barrido',
  'settings.sharing.selectCollection': 'Elige una colección…',
  'settings.sharing.selectSweep': 'Elige un barrido…',
  'settings.sharing.createLink': 'Crear enlace',
  'settings.sharing.empty.title': 'Aún no hay enlaces compartidos',
  'settings.sharing.empty.body': 'Crea un enlace público para una colección o un barrido — solo lectura.',
  'settings.sharing.copyLink': 'Copiar enlace',
  'settings.sharing.revoke': 'Revocar',
  'settings.toast.linkCreated': 'Enlace creado',
  'settings.toast.linkCreatedBody': 'Cualquiera con el enlace puede verlo — solo lectura.',
  'settings.toast.linkFailed': 'No se pudo crear el enlace',
  'settings.toast.linkFailedBody': 'Inténtalo de nuevo en un momento.',
  'settings.toast.linkRevoked': 'Enlace revocado',
  'settings.toast.linkRevokedBody': 'Ese enlace ya no se abre.',
  'settings.toast.revokeFailed': 'No se pudo revocar el enlace',
  'settings.toast.revokeFailedBody': 'Inténtalo de nuevo.',
  'settings.toast.copied': 'Copiado',
  'settings.toast.copiedBody': 'El enlace está en tu portapapeles.',
  'settings.toast.deleteFailed': 'Error al eliminar',
  'settings.toast.deleteFailedBody': 'No pudimos eliminar la cuenta. Inténtalo de nuevo.',
  'settings.danger.export': 'Exportar todos los datos',
  'settings.danger.deleteAccount': 'Eliminar cuenta y datos',
  'settings.danger.note': 'Exporta primero — la eliminación es permanente tras 14 días.',
  'settings.storage.saved': '{count} guardados',
  'settings.storage.tabs': 'PESTAÑAS',
  'settings.storage.sessions': 'SESIONES',
  'settings.storage.indexed': 'INDEXADAS',
  'settings.appearance.dark': 'Tema oscuro',
  'settings.appearance.darkDesc': 'Azul marino profundo — el aspecto por defecto',
  'settings.language.label': 'Idioma de la interfaz',
  'settings.language.desc': 'Se aplica al instante, en toda la app.',
  'settings.promo.title': 'Instala la extensión',
  'settings.promo.body': 'Barre una ventana entera con un clic, directamente desde tu barra de herramientas.',
  'settings.about.webApp': 'App web',
  'settings.about.extension': 'Extensión',
  'settings.deleteModal.title': 'Eliminar cuenta y datos',
  'settings.deleteModal.subtitle': 'Esto elimina tu vault en todas partes.',
  'settings.deleteModal.body':
    'Cada barrido, página guardada y colección quedará programada para eliminarse. Se puede recuperar durante 14 días; después desaparece para siempre. Exporta tus datos primero si podrías querer recuperarlos.',
  'settings.deleteModal.keep': 'Conservar cuenta',
  'settings.deleteModal.confirm': 'Eliminar todo',
  'settings.planModal.title': 'Gestiona tu plan Pro',
  'settings.planModal.subtitle': 'Free sigue siendo generoso — Pro lo conecta todo.',
  'settings.planModal.body':
    'Cancela cuando quieras y conservarás Pro hasta el final del período facturado. Después: los guardados locales siguen sin límite, la sincronización baja a un navegador, y la búsqueda IA y las colecciones se pausan.',
  'settings.planModal.nothingDeleted': 'No se elimina nada.',
  'settings.planModal.emailSupport': 'Escribir a soporte para cancelar',
  'settings.planModal.keepPro': 'Mantener Pro',

  // ── login / verify ─────────────────────────────────────────────────────
  'login.welcome': 'Bienvenido a Nomad Tabs',
  'login.tagline': 'Un solo vault para todos tus navegadores.',
  'login.emailPlaceholder': 'tu@email.com',
  'login.sendLink': 'Enviar enlace mágico',
  'login.error.tooMany': 'Demasiadas solicitudes — espera un minuto e inténtalo de nuevo.',
  'login.error.sendFailed': 'No pudimos enviar el enlace ahora mismo. Inténtalo en un momento.',
  'login.reassure': 'Tus páginas guardadas son privadas — solo tú puedes abrir tu vault.',
  'login.checkInbox': 'Revisa tu bandeja de entrada',
  'login.sentTo': 'Enviamos un enlace de acceso a',
  'login.expires': 'EL ENLACE CADUCA EN 15 MIN',
  'login.nothingThere': '¿No llega nada?',
  'login.checkSpam': 'revisa el spam',
  'login.wrongAddress': '← ¿Dirección equivocada? Volver',
  'login.devDivider': 'DEV · PEGA EL TOKEN',
  'login.tokenPlaceholder': 'token de la consola de la API',
  'verify.signingIn': 'Iniciando sesión…',
  'verify.hangTight': 'Un momento, esto solo tarda un segundo.',
  'verify.expired.title': 'Este enlace ha caducado',
  'verify.expired.body':
    'Los enlaces mágicos valen 15 minutos y un solo uso. Pide uno nuevo y entrarás al instante.',
  'verify.backToSignIn': 'Volver a iniciar sesión',

  // ── onboarding ─────────────────────────────────────────────────────────
  'onboarding.hero.title1': 'Tu navegador por fin',
  'onboarding.hero.title2': 'tiene memoria.',
  'onboarding.hero.body':
    'Barre ventanas enteras a tu vault, ciérralas sin miedo y encuentra cualquier página de nuevo — por lo que había escrito en ella.',
  'onboarding.getStarted': 'Empezar',
  'onboarding.hero.micro': 'TOMA 60 SEGUNDOS · NO HACE FALTA CUENTA PARA EXPLORAR',
  'onboarding.perm.title': 'Por qué tu navegador pide acceso',
  'onboarding.perm.lede': 'El próximo aviso suena aterrador. Esto es lo que significa en realidad.',
  'onboarding.perm.head': '«LEER Y CAMBIAR TODOS TUS DATOS EN TODOS LOS SITIOS» — TRADUCIDO',
  'onboarding.perm.p1.title': 'Leemos las páginas solo para hacerlas buscables',
  'onboarding.perm.p1.body': 'Para que luego encuentres «ese artículo sobre las tasas hipotecarias».',
  'onboarding.perm.p2.title': 'Tus páginas quedan privadas en tu dispositivo',
  'onboarding.perm.p2.body': 'Solo tú puedes abrir tu vault — no vemos lo que guardas.',
  'onboarding.perm.p3.title': 'Bancos, correo y sitios privados pueden excluirse',
  'onboarding.perm.p3.body': 'Una lista en Ajustes — ahí el contenido nunca se captura.',
  'onboarding.perm.continue': 'Suena justo — continuar',
  'onboarding.perm.later': 'Pregúntame luego',
  'onboarding.perm.micro': 'PUEDES CAMBIARLO CUANDO QUIERAS EN AJUSTES',
  'onboarding.rescue.title': 'Rescata tus pestañas ahora mismo',
  'onboarding.rescue.lede': 'Elige una opción — ambas toman menos de un minuto y nada se pierde.',
  'onboarding.rescue.onetab.title': 'Importar desde OneTab',
  'onboarding.rescue.onetab.body':
    'Trae todo tu cementerio de OneTab con un clic — los grupos se vuelven sesiones.',
  'onboarding.rescue.chooseFile': 'Elegir archivo',
  'onboarding.rescue.onetab.foot': 'TAMBIÉN: SESSION BUDDY · HTML DE MARCADORES',
  'onboarding.rescue.ext.title': 'Instala la extensión del navegador',
  'onboarding.rescue.ext.body':
    'Barre una ventana entera con un clic, desde tu barra de herramientas. Mira cómo vuelve tu RAM.',
  'onboarding.rescue.ext.foot': 'CHROME · EDGE · YANDEX · COMET',
  'onboarding.rescue.openApp': 'Abrir la app web →',

  // ── pricing ────────────────────────────────────────────────────────────
  'pricing.title': 'Pásate a Pro cuando estés listo',
  'pricing.subtitle': 'Free sigue siendo generoso. Pro lo conecta todo.',
  'pricing.monthly': 'Mensual ${price}',
  'pricing.yearly': 'Anual ${price}',
  'pricing.forever': 'PARA SIEMPRE',
  'pricing.yourPlan': 'TU PLAN',
  'pricing.mostLoved': 'EL MÁS QUERIDO',
  'pricing.perMonth': '/MES',
  'pricing.perMonthYearly': '/MES · ${total} AL AÑO',
  'pricing.free.f1': 'Guardados locales ilimitados',
  'pricing.free.f2': '1 navegador sincronizado a la nube',
  'pricing.free.f3': 'Búsqueda por título y URL',
  'pricing.free.f4': 'Búsqueda de texto completo e IA',
  'pricing.free.f5': 'Colecciones con IA',
  'pricing.pro.f1': 'Todos los navegadores y dispositivos sincronizados',
  'pricing.pro.f2': 'Texto completo + búsqueda semántica con IA',
  'pricing.pro.f3': 'Colecciones IA que se ordenan solas',
  'pricing.pro.f4': 'Historial de versiones de páginas guardadas',
  'pricing.pro.f5': 'Soporte prioritario',
  'pricing.stayFree': 'Seguir en Free',
  'pricing.youreOnPro': 'Ya tienes Pro',
  'pricing.signInToUpgrade': 'Inicia sesión para mejorar',
  'pricing.checkoutSoon': 'El pago se abre pronto — te escribiremos en cuanto esté disponible.',
  'pricing.checkoutFailed': 'Algo falló al iniciar el pago. Inténtalo de nuevo.',
  'pricing.footer': 'CANCELA CUANDO QUIERAS · EXPORTA TUS DATOS SIEMPRE · LOS GUARDADOS LOCALES NUNCA SE BLOQUEAN',

  // ── billing / version history ──────────────────────────────────────────
  'billing.title': 'Facturación y pagos',
  'versions.title': 'Historial de versiones',
  'versions.current': 'ACTUAL',
  'versions.restoreThis': 'Restaurar esta versión',

  // ── trash ──────────────────────────────────────────────────────────────
  'trash.title': 'Papelera',
  'trash.autoPurge': 'PURGA AUTOMÁTICA A LOS 14 DÍAS',
  'trash.itemsLoading': '… ELEMENTOS',
  'trash.restoreSelected': 'Restaurar seleccionados ({count})',
  'trash.restoring': 'Restaurando…',
  'trash.emptyTrash': 'Vaciar papelera',
  'trash.error.title': 'No se pudo cargar la papelera',
  'trash.emptyState.title': 'La papelera está vacía',
  'trash.emptyState.body':
    'Las sesiones y páginas eliminadas esperan aquí 14 días, luego se purgan automáticamente.',
  'trash.footNote': 'ELIMINAR DE LA PAPELERA ES PERMANENTE · EXPORTA TUS DATOS EN AJUSTES → ZONA DE PELIGRO',
  'trash.purge.title': '¿Eliminar para siempre?',
  'trash.purge.subtitle': '«{title}» se eliminará permanentemente — sin los 14 días de recuperación.',
  'trash.purge.confirm': 'Eliminar para siempre',
  'trash.purge.sweepBody': 'Esto elimina la sesión y sus {tabs} definitivamente.',
  'trash.purge.pageBody': 'Esto elimina la página guardada y su contenido capturado definitivamente.',
  'trash.emptyConfirm.title': '¿Vaciar la papelera?',
  'trash.emptyConfirm.subtitle': 'Elimina permanentemente {entries}.',
  'trash.emptyConfirm.body':
    'Todo lo que hay en la papelera desaparece para siempre — no se puede deshacer. Puedes exportar tus datos cuando quieras en Ajustes → Zona de peligro.',
  'trash.toast.restored': 'Restaurado',
  'trash.toast.restoredBody': '«{title}» está de vuelta en tu vault.',
  'trash.toast.restoredMany': 'Restauradas {entries}',
  'trash.toast.restoreFailed': 'No se pudo restaurar',
  'trash.toast.restoreManyFailed': 'No se pudo restaurar la selección',
  'trash.toast.purged': 'Eliminado para siempre',
  'trash.toast.purgedBody': '«{title}» ha desaparecido definitivamente.',
  'trash.toast.purgeFailed': 'No se pudo eliminar',
  'trash.toast.emptied': 'Papelera vaciada',
  'trash.toast.emptiedBody': 'Todo lo que había en la papelera se eliminó permanentemente.',
  'trash.toast.emptyFailed': 'No se pudo vaciar la papelera',
};
